// Small presentational building blocks shared by the New Season editors:
// a React-state modal, the "Delete Block" confirm, a toast and segmented tabs.
const React = require('react');
const ReactDOM = require('react-dom');
const { useState, useEffect, useCallback, useRef } = require('react');
const { useTranslation } = require('react-i18next');
const { TOAST_DURATION_MS } = require('./newSeasonConstants');

const trashIcon = require('images/icons/trash.svg');
const successIcon = require('images/icons/success_icon.svg');

const Modal = ({ isOpen, onClose, children, className }) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }
  return ReactDOM.createPortal(
    <div className='ns-modal-backdrop' onMouseDown={onClose}>
      <div
        className={'ns-modal ' + (className || '')}
        role='dialog'
        aria-modal='true'
        onMouseDown={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

// `title` / `text` default to the single-block wording.
const ConfirmDeleteBlockModal = ({
  isOpen,
  title,
  text,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onCancel} className='ns-modal-small'>
      <div className='ns-delete-modal'>
        <div className='ns-delete-icon'>
          <img src={trashIcon} alt='' />
        </div>
        <div className='ns-modal-title'>
          {title ?? t('new_season_delete_block_title')}
        </div>
        <div className='ns-modal-text'>
          {text ?? t('new_season_delete_block_text')}
        </div>
        <div className='ns-modal-footer'>
          <button
            type='button'
            className='ns-btn ns-btn-outline'
            onClick={onCancel}
          >
            {t('cancel')}
          </button>
          <button
            type='button'
            className='ns-btn ns-btn-danger'
            onClick={onConfirm}
          >
            {t('delete')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// const { toast, showToast } = useToast(); render <Toast toast={toast} />
const useToast = () => {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const showToast = useCallback((message, kind = 'success') => {
    clearTimeout(timerRef.current);
    setToast({ message, kind, id: Date.now() });
    timerRef.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  return { toast, showToast };
};

const Toast = ({ toast }) => {
  if (!toast) {
    return null;
  }
  return (
    <div className={'ns-toast ns-toast-' + toast.kind} role='status'>
      {toast.kind === 'success' && <img src={successIcon} alt='' />}
      <span>{toast.message}</span>
    </div>
  );
};

// options: [{value, label, disabled, hasUnsavedChanges}]; an option with
// unsaved changes shows a dot after its label.
const SegmentedTabs = ({ options, value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className='ns-segmented' role='tablist'>
      {options.map((option) => (
        <button
          type='button'
          role='tab'
          key={option.value}
          aria-selected={option.value === value}
          disabled={option.disabled}
          className={
            'ns-segment' + (option.value === value ? ' ns-segment-active' : '')
          }
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.hasUnsavedChanges && (
            <span
              className='ns-segment-dot'
              title={t('new_season_tab_unsaved')}
              aria-label={t('new_season_tab_unsaved')}
            />
          )}
        </button>
      ))}
    </div>
  );
};

const LoadingState = () => {
  const { t } = useTranslation();
  return (
    <div className='ns-loading'>
      <span className='spinner-border spinner-border-sm mr-2' role='status' />
      {t('loading')}
    </div>
  );
};

module.exports = {
  Modal,
  ConfirmDeleteBlockModal,
  useToast,
  Toast,
  SegmentedTabs,
  LoadingState,
};
